import { useAbility } from '@mdvip/shared/stores/authStore';
import type { AppAbility } from '@mdvip/shared/types/ability';
import { useSearchParams } from 'react-router-dom';
import { canAccessView, type DatasetKey, VIEWS, type ViewEntry } from '@/lib/member-views/views';
import { MyPatientDirectoryPlaceholder } from './MyPatientDirectoryPlaceholder';

/** The view the directory opens on, and falls back to. */
const DEFAULT_VIEW_KEY: DatasetKey = 'all-active';

/**
 * The view `?view=` asks for, or the default when it names nothing this user can be shown: unknown,
 * not built yet, or behind a permission they don't hold.
 */
function resolveView(requested: string | null, ability: AppAbility | null) {
  const showable = (key: string | null): ViewEntry | undefined => {
    const view = VIEWS.find((entry) => entry.key === key);
    return view?.available && canAccessView(view, ability) ? view : undefined;
  };
  return showable(requested) ?? showable(DEFAULT_VIEW_KEY);
}

/**
 * MyPatientDirectoryPage — the directory under Patients. Renders the view `?view=` names, falling
 * back to All active members when it's missing, unknown, not built yet or not permitted.
 *
 * The view is keyed by its key, so switching views mounts the new one fresh: its dataset's
 * `useRows()` never trades places with another view's inside one component.
 */
export function MyPatientDirectoryPage() {
  const [searchParams] = useSearchParams();
  const view = resolveView(searchParams.get('view'), useAbility());

  if (!view?.component) return <MyPatientDirectoryPlaceholder />;
  const View = view.component;
  return <View key={view.key} />;
}
